import { getElementTypeLabel } from '../../../constants/AssetTestConfig';

/**
 * The three sources ReportsView merges into one list. A session row is a
 * completed test/repair session; the two bucket kinds come from the
 * repair_report_buckets endpoints (Brief 59/60) and carry no total_assets/
 * passed_count/element_type in the session sense — see fails-null handling below.
 */
export const ROW_KIND = {
  SESSION: 'session',
  FINALISED_BUCKET: 'finalised-bucket',
  OPEN_BUCKET: 'open-bucket',
};

/**
 * Unique key across all three sources. Session ids are strings, finalised
 * bucket ids are integers from a different table — a bare `session.id ===
 * bucket.id` collision is possible, so every key is kind-prefixed. An open
 * bucket has no id at all (it's implicit: unfinalised repairs for a site +
 * element type), so its identity is that pair.
 */
export function rowKey(kind, source) {
  switch (kind) {
    case ROW_KIND.SESSION:
      return `session:${source.id}`;
    case ROW_KIND.FINALISED_BUCKET:
      return `bucket:${source.id}`;
    case ROW_KIND.OPEN_BUCKET:
      return `open:${source.siteId}:${source.elementType}`;
    default:
      throw new Error(`rowKey: unknown row kind "${kind}"`);
  }
}

/** Sortable timestamp (ms) for a row. Missing dates fall back to epoch rather than NaN. */
export function sortDate({ kind, raw }) {
  if (kind === ROW_KIND.SESSION) {
    return new Date(raw.end_time || raw.start_time || 0).getTime();
  }
  if (kind === ROW_KIND.FINALISED_BUCKET) {
    return new Date(raw.finalisedAt || 0).getTime();
  }
  // Open buckets have no single event date; the latest repair folded into it
  // is the most meaningful "how stale is this" signal for sorting.
  return new Date(raw.latestRepairedAt || raw.earliestRepairedAt || 0).getTime();
}

/**
 * Fail count is a test-session concept (total_assets - passed_count). Neither
 * bucket kind has anything analogous — a bucket is repairs already done, not
 * tests that failed — so this returns `null`, not `0`, to keep "no fails" and
 * "not applicable" distinct for renderers and the fails sort comparator.
 */
export function getFailCount({ kind, raw }) {
  if (kind !== ROW_KIND.SESSION) return null;
  const total = raw.total_assets ?? 0;
  const passed = raw.passed_count ?? 0;
  return Math.max(0, total - passed);
}

function getSessionReportType(session) {
  const passed = session.passed_count ?? 0;
  const total = session.total_assets ?? 0;
  const defects = total > 0 ? total - passed : 0;
  return defects > 0 ? 'defect' : 'session';
}

/** 'session' | 'defect' | 'repair' — drives the badge and the type sort rank. */
export function getRowReportType({ kind, raw }) {
  if (kind === ROW_KIND.FINALISED_BUCKET || kind === ROW_KIND.OPEN_BUCKET) return 'repair';
  return getSessionReportType(raw);
}

const BADGE_BY_REPORT_TYPE = {
  defect: { label: 'Defect', variant: 'danger' },
  repair: { label: 'Repair', variant: 'accent' },
  session: { label: 'Session', variant: 'muted' },
};

/** { label, variant } for the Badge component. Open buckets read "Needs Report" — nothing has been signed off yet. */
export function getRowBadge(row) {
  if (row.kind === ROW_KIND.OPEN_BUCKET) return { label: 'Needs Report', variant: 'warning' };
  return BADGE_BY_REPORT_TYPE[getRowReportType(row)] ?? BADGE_BY_REPORT_TYPE.session;
}

function getSiteLabel({ kind, raw }) {
  if (kind === ROW_KIND.SESSION) return raw.reportsSiteLabel || 'Site';
  return raw.siteName || 'Site';
}

function getTechnicianLabel({ kind, raw }) {
  if (kind === ROW_KIND.SESSION) return raw.user_name || null;
  if (kind === ROW_KIND.FINALISED_BUCKET) return raw.finalisedByName || null;
  return null; // open bucket: nobody has signed off on it yet
}

function getQty({ kind, raw }) {
  if (kind === ROW_KIND.SESSION) return raw.total_assets ?? 0;
  return raw.repairCount ?? 0; // both bucket kinds carry repairCount
}

/** Session rows and open buckets have no report number yet — only a finalised bucket does. */
function getReportId({ kind, raw }) {
  return kind === ROW_KIND.FINALISED_BUCKET ? (raw.reportId || '') : '';
}

function buildRow(kind, raw) {
  const row = { kind, raw };
  return {
    ...row,
    key: rowKey(kind, raw),
    siteId: kind === ROW_KIND.SESSION ? raw.reportsSiteId : raw.siteId,
    siteLabel: getSiteLabel(row),
    elementType: kind === ROW_KIND.SESSION ? (raw.element_type || 'electrical') : raw.elementType,
    technician: getTechnicianLabel(row),
    dateMs: sortDate(row),
    qty: getQty(row),
    fails: getFailCount(row),
    reportId: getReportId(row),
    reportType: getRowReportType(row),
    badge: getRowBadge(row),
  };
}

/**
 * True for the empty "repair session" shell rows `/sessions/site/:id` still
 * returns (session_type containing 'repair') — the test_sessions row
 * persistCompletedRepairSession writes with zero test_results. Applied in
 * useReportRows before normalising, so these never reach the row list
 * (Brief 59 Part 5); the real repair report is the bucket row instead.
 */
export function isRepairShellSession(session) {
  return String(session?.session_type || '').toLowerCase().includes('repair');
}

/** Normalise a completed test/repair session (from the existing sessions query) into a common row. */
export function normaliseSessionRow(session) {
  return buildRow(ROW_KIND.SESSION, session);
}

/** Normalise one `GET /api/repairs/buckets` row (already signed off) into a common row. */
export function normaliseFinalisedBucketRow(bucket) {
  return buildRow(ROW_KIND.FINALISED_BUCKET, bucket);
}

/** Normalise one `GET /api/repairs/buckets/open` row (not yet finalised) into a common row. */
export function normaliseOpenBucketRow(bucket) {
  return buildRow(ROW_KIND.OPEN_BUCKET, bucket);
}

const TYPE_RANK = { repair: 0, defect: 1, session: 2 };

/**
 * One comparator per REPORT_SORT_KEYS value. Each already applies `mul` (the
 * asc/desc multiplier) so `fails` can special-case null placement instead of
 * having direction re-applied uniformly afterwards — see compareReportRows.
 */
const ROW_COMPARATORS = {
  date: (a, b, mul) => mul * (a.dateMs - b.dateMs),
  site: (a, b, mul) => mul * a.siteLabel.localeCompare(b.siteLabel, undefined, { sensitivity: 'base' }),
  technician: (a, b, mul) =>
    mul * (a.technician || '').localeCompare(b.technician || '', undefined, { sensitivity: 'base' }),
  component: (a, b, mul) =>
    mul * getElementTypeLabel(a.elementType).localeCompare(getElementTypeLabel(b.elementType), undefined, {
      sensitivity: 'base',
    }),
  type: (a, b, mul) => mul * ((TYPE_RANK[a.reportType] ?? 2) - (TYPE_RANK[b.reportType] ?? 2)),
  qty: (a, b, mul) => mul * (a.qty - b.qty),
  // Bucket rows have no fails concept (`fails: null`) — they always sort after
  // every session row regardless of asc/desc, rather than flipping to the top
  // on "ascending" as a bare numeric comparison would do with null coerced to 0.
  fails: (a, b, mul) => {
    if (a.fails == null && b.fails == null) return 0;
    if (a.fails == null) return 1;
    if (b.fails == null) return -1;
    return mul * (a.fails - b.fails);
  },
};

/** Sort two normalised rows by `sortKey` ('date' | 'site' | 'technician' | 'component' | 'type' | 'qty' | 'fails'). */
export function compareReportRows(a, b, sortKey, sortDir) {
  const mul = sortDir === 'asc' ? 1 : -1;
  const comparator = ROW_COMPARATORS[sortKey] || ROW_COMPARATORS.date;
  const cmp = comparator(a, b, mul);
  if (cmp !== 0) return cmp;
  return a.key.localeCompare(b.key);
}
